import React, { useEffect, useState } from "react";

import { Link, useParams } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";

import Loading from "components/Loading";
import Panel from "components/Panel";
import PageContent from "components/PageContent";
import PanelSection from "components/PanelSection";
import SettingsNav from "components/SettingsNav";
import { useAppConfig } from "components/WithAppConfig";
import { useRoutes } from "utils/routes";

import ActivatePlan from "./ActivatePlan";
import UpdateCreditCard from "./UpdateCreditCard";
import UpdateBillingDetails from "./UpdateBillingDetails";
import CancelPlan from "./CancelPlan";
import PlanInfo from "./PlanInfo";
import Overview from "./Overview";
import PlanComparison from "./PlanComparison";
import ServerUsagePanel from "./ServerUsagePanel";

import { useBillingQuery } from "./gql/Query.generated";
import { PlanInfoType } from "./PlanInfo";

type Props = {
  tab:
    | "overview"
    | "update_credit_card"
    | "update_billing_details"
    | "activate_plan"
    | "cancel_plan"
    | "compare";
};

const Billing: React.FunctionComponent<Props> = ({ tab }) => {
  const { slug: organizationSlug } = useParams();

  const { loading, error, data } = useBillingQuery({
    variables: {
      organizationSlug,
    },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const {
    billedThroughAwsMarketplace,
    permittedToBilling,
    planInfo,
    serverLimit,
    serverCountForBilling,
  } = data.getOrganizationDetails;

  // Treat the lite plan as an expired trial in the plan comparison
  const expiredTrial =
    planInfo.expiredTrial ||
    (!planInfo.isTrial &&
      !planInfo.activeSubscription &&
      planInfo.supportsLitePlan);

  return (
    <PageContent
      title="Settings"
      pageCategory="subscription"
      pageName="index"
      pageTab={tab}
      featureNav={<SettingsNav />}
    >
      {billedThroughAwsMarketplace ? (
        <BilledThroughAwsMarketplace
          planInfo={planInfo}
          organizationSlug={organizationSlug}
          serverCount={serverCountForBilling}
          serverLimit={serverLimit}
        />
      ) : permittedToBilling ? (
        <WithStripeElements>
          {tab == "overview" ? (
            <Overview planInfo={planInfo} />
          ) : tab == "update_credit_card" ? (
            <UpdateCreditCard organizationSlug={organizationSlug} />
          ) : tab == "update_billing_details" ? (
            <UpdateBillingDetails organizationSlug={organizationSlug} />
          ) : tab == "activate_plan" ? (
            <ActivatePlan organizationSlug={organizationSlug} />
          ) : tab == "cancel_plan" ? (
            <CancelPlan />
          ) : tab == "compare" ? (
            <PlanComparison
              isTrial={planInfo.isTrial}
              trialEndsAt={planInfo.trialEndsAt}
              expiredTrial={expiredTrial}
            />
          ) : null}
        </WithStripeElements>
      ) : (
        <NoBillingAccess
          planInfo={planInfo}
          organizationSlug={organizationSlug}
          serverCount={serverCountForBilling}
          serverLimit={serverLimit}
        />
      )}
    </PageContent>
  );
};

const WithStripeElements = ({ children }: { children: React.ReactNode }) => {
  const { stripePublishableKey } = useAppConfig();
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    if (stripePromise === null && stripePublishableKey !== null) {
      setStripePromise(loadStripe(stripePublishableKey));
    }
  }, [stripePromise, stripePublishableKey]);

  return (
    <Elements stripe={stripePromise} options={{ locale: "en" }}>
      {children}
    </Elements>
  );
};

const NoBillingAccess: React.FunctionComponent<{
  organizationSlug: string;
  serverLimit: number;
  serverCount: number;
  planInfo: PlanInfoType;
}> = ({ planInfo, organizationSlug, serverCount, serverLimit }) => {
  const { organizationMembers } = useRoutes();
  return (
    <>
      <Panel title="Subscription">
        <PanelSection>
          <PlanInfo planInfo={planInfo} allowPlanChange={false} />
          You don't have the <strong>Billing</strong> permission required to
          make changes. You can view the{" "}
          <Link to={organizationMembers(organizationSlug)}>Members</Link> list
          to see which team members have the <strong>Billing</strong>{" "}
          permission.
        </PanelSection>
      </Panel>
      <ServerUsagePanel
        organizationSlug={organizationSlug}
        usage={serverCount}
        limit={serverLimit}
        extraServersAction="omit"
        subscriptionPlanId={planInfo.subscriptionPlanId}
      />
    </>
  );
};

const BilledThroughAwsMarketplace: React.FunctionComponent<{
  organizationSlug: string;
  serverLimit: number;
  serverCount: number;
  planInfo: PlanInfoType;
}> = ({ planInfo, organizationSlug, serverCount, serverLimit }) => {
  return (
    <>
      <Panel title="Subscription">
        <PanelSection>
          <PlanInfo planInfo={planInfo} allowPlanChange={false} />
          Your subscription is billed through{" "}
          <a
            href="https://console.aws.amazon.com/marketplace/subscriptions"
            target="_blank"
          >
            AWS Marketplace
          </a>
          . You can adjust or cancel your subscription there.
        </PanelSection>
      </Panel>
      <ServerUsagePanel
        organizationSlug={organizationSlug}
        usage={serverCount}
        limit={serverLimit}
        extraServersAction="omit"
        subscriptionPlanId={planInfo.subscriptionPlanId}
      />
    </>
  );
};

export default Billing;
